import { KeyIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input } from '@nextui-org/react';
import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { ModernModal } from '@portal/components/common/ModernModal.ui';
import { PasswordInput } from '@portal/components/common/PasswordInput.ui';
import { TypographyUI } from '@portal/components/common/Typography.ui';
import { tsrestClient } from '@portal/provider/tsrest';

const recoverKeySchema = z.object({
  recoveryCode: z.string().min(1, 'Recovery code is required'),
  masterPassword: z.string().min(8, 'Master password must be at least 8 characters'),
});

type RecoverKeyForm = z.infer<typeof recoverKeySchema>;

interface KeyRecoveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Key Recovery Modal Component
 *
 * Handles system migration when encryption key is invalid or system has changed
 */
export const KeyRecoveryModal = ({ isOpen, onClose, onSuccess }: KeyRecoveryModalProps) => {
  const [newRecoveryCode, setNewRecoveryCode] = useState<string | null>(null);
  const [isRecovering, setIsRecovering] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const recoverKeyMutation = tsrestClient.encryption.recoverKey.useMutation({
    onSuccess: async data => {
      setNewRecoveryCode(data.body.newRecoveryCode);
      toast('System migration completed successfully!', { type: 'success' });

      // Set a temporary flag to prevent AuthGuard from redirecting during recovery
      localStorage.setItem('encryption_recovery_in_progress', 'true');
    },
    onError: error => {
      toast(
        <>
          <TypographyUI className='font-bold'>Failed to recover encryption key</TypographyUI>
          <TypographyUI className='text-ts'>error: {(error.body as any).message}</TypographyUI>
        </>,
        { type: 'error' }
      );
    },
  });

  const form = useForm<RecoverKeyForm>({
    resolver: zodResolver(recoverKeySchema),
    mode: 'all',
    defaultValues: {
      recoveryCode: '',
      masterPassword: '',
    },
  });

  const {
    control,
    formState: { errors, isValid, isDirty },
    handleSubmit,
    reset,
  } = form;

  const onSubmit = async (data: RecoverKeyForm) => {
    setIsRecovering(true);
    try {
      await recoverKeyMutation.mutateAsync({
        body: {
          recoveryCode: data.recoveryCode,
          masterPassword: data.masterPassword,
        },
      });
    } finally {
      setIsRecovering(false);
    }
  };

  const handleClose = () => {
    setNewRecoveryCode(null);
    setHasConfirmed(false);
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    setHasConfirmed(true);
    // Remove the temporary flag so AuthGuard can redirect normally
    localStorage.removeItem('encryption_recovery_in_progress');
    // Call success callback if provided
    if (onSuccess) {
      onSuccess();
    }
    handleClose();
  };

  return (
    <ModernModal
      isOpen={isOpen}
      onOpenChange={() => handleClose()}
      title={newRecoveryCode ? 'Recovery Completed Successfully' : 'Recover Encryption Key'}
      subtitle={
        newRecoveryCode
          ? 'Your encryption key has been recovered. Please save the new recovery code.'
          : 'Enter your recovery code and master password to restore access to your encrypted data.'
      }
      icon={<KeyIcon className='h-5 w-5' />}
      size='2xl'
      footer={
        newRecoveryCode ? (
          <div className='flex gap-2'>
            <Button color='primary' onPress={handleConfirm} isLoading={hasConfirmed}>
              {hasConfirmed ? 'Proceeding...' : 'I Have Stored the New Recovery Code'}
            </Button>
          </div>
        ) : (
          <>
            <Button color='danger' variant='light' onPress={handleClose}>
              Cancel
            </Button>
            <Button
              color='primary'
              isLoading={isRecovering}
              isDisabled={!isValid || !isDirty}
              onClick={handleSubmit(onSubmit)}
            >
              Recover Key
            </Button>
          </>
        )
      }
    >
      {newRecoveryCode ? (
        <div className='space-y-3'>
          <div className='p-3 bg-green-50 border border-green-200 rounded-lg'>
            <TypographyUI variant='body' color='green' className='font-medium mb-1'>
              ✅ System Migration Completed Successfully
            </TypographyUI>
            <TypographyUI variant='body' color='gray' className='text-sm'>
              Your encryption key has been recovered and is now working with your current system.
            </TypographyUI>
          </div>

          <div className='p-3 bg-blue-50 border border-blue-200 rounded-lg'>
            <TypographyUI variant='body' color='blue-gray' className='font-medium mb-2'>
              🔑 New Recovery Code
            </TypographyUI>
            <div className='flex items-center gap-2'>
              <div className='flex-1 p-2 bg-white border border-blue-300 rounded font-mono text-sm break-all'>
                {newRecoveryCode}
              </div>
              <Button
                size='sm'
                variant='flat'
                color='primary'
                className='min-w-0 px-2'
                onPress={async () => {
                  try {
                    await navigator.clipboard.writeText(newRecoveryCode);
                    toast('Recovery code copied to clipboard', { type: 'success' });
                  } catch (error) {
                    toast('Failed to copy recovery code', { type: 'error' });
                  }
                }}
              >
                <ClipboardDocumentIcon className='h-4 w-4' />
              </Button>
            </div>
            <TypographyUI variant='body' color='gray' className='text-xs mt-2'>
              Save this new recovery code securely. The old recovery code is no longer valid.
            </TypographyUI>
          </div>

          <div className='p-3 bg-yellow-50 border border-yellow-200 rounded-lg'>
            <TypographyUI variant='body' color='orange' className='font-medium mb-1'>
              ⚠️ Security Notice
            </TypographyUI>
            <TypographyUI variant='body' color='gray' className='text-xs'>
              • Store recovery code securely (password manager, safe deposit box)
              <br />
              • Old recovery code is no longer valid for this system
              <br />• Keep master password secure for future recovery
            </TypographyUI>
          </div>
        </div>
      ) : (
        <div className='space-y-3'>
          <div className='p-3 bg-blue-50 border border-blue-200 rounded-lg'>
            <TypographyUI variant='body' color='blue-gray' className='font-medium mb-1'>
              🔐 Recovery Information Required
            </TypographyUI>
            <TypographyUI variant='body' color='gray' className='text-sm'>
              Enter your recovery code and master password to restore access to your encrypted data.
            </TypographyUI>
          </div>

          <div className='space-y-3'>
            <Controller
              name='recoveryCode'
              control={control}
              render={({ field }) => (
                <Input
                  label='Recovery Code'
                  variant='bordered'
                  value={field.value}
                  onValueChange={field.onChange}
                  isInvalid={!!errors.recoveryCode}
                  errorMessage={errors.recoveryCode?.message}
                  placeholder='Enter your recovery code'
                  className='font-mono text-sm'
                  onFocus={e => {
                    const target = e.target as HTMLInputElement;
                    target.select();
                  }}
                />
              )}
            />

            <Controller
              name='masterPassword'
              control={control}
              render={({ field }) => (
                <PasswordInput
                  label='Master Password'
                  variant='bordered'
                  value={field.value}
                  onValueChange={field.onChange}
                  isInvalid={!!errors.masterPassword}
                  errorMessage={errors.masterPassword?.message}
                  placeholder='Enter your master password'
                />
              )}
            />
          </div>

          <div className='p-3 bg-gray-50 border border-gray-200 rounded-lg'>
            <TypographyUI variant='body' color='gray' className='text-sm'>
              <strong>Note:</strong> The recovery code and master password are used to decrypt your application key and
              restore access to your encrypted data. Make sure you have both available.
            </TypographyUI>
          </div>
        </div>
      )}
    </ModernModal>
  );
};
